import { Behavior, SceneObject } from '../types';

// The small, runnable set of behaviors that turn a static scene into a game.
// Keep this list tight — every entry here is actually executed by the Play runtime.
export type BehaviorType =
  | 'player'
  | 'collectible'
  | 'goal'
  | 'obstacle'
  | 'hazard';

export interface BehaviorParamSpec {
  key: string;
  label: string;
  type: 'number' | 'boolean';
  min?: number;
  max?: number;
  step?: number;
  default: number | boolean;
}

export interface BehaviorMeta {
  type: BehaviorType;
  label: string;
  description: string;
  defaultParams: Record<string, unknown>;
  // Editable parameters surfaced in the inspector. Empty = nothing to tune.
  params: BehaviorParamSpec[];
}

export const BEHAVIOR_CATALOG: BehaviorMeta[] = [
  {
    type: 'player',
    label: 'Player',
    description: 'Move this with the arrow keys or WASD in Play mode.',
    defaultParams: { speed: 6, lives: 3 },
    params: [
      { key: 'speed', label: 'Speed', type: 'number', min: 1, max: 20, step: 1, default: 6 },
      { key: 'lives', label: 'Lives', type: 'number', min: 1, max: 9, step: 1, default: 3 },
    ],
  },
  {
    type: 'collectible',
    label: 'Collectible',
    description: 'The player picks this up on contact; counts toward the goal.',
    defaultParams: { points: 1 },
    params: [
      { key: 'points', label: 'Points', type: 'number', min: 1, max: 100, step: 1, default: 1 },
    ],
  },
  {
    type: 'goal',
    label: 'Goal',
    description: 'Reach this to win — after collecting everything, by default.',
    defaultParams: { requireAllCollectibles: true, timeLimit: 0 },
    params: [
      {
        key: 'requireAllCollectibles',
        label: 'Need all collectibles',
        type: 'boolean',
        default: true,
      },
      {
        key: 'timeLimit',
        label: 'Time limit (sec, 0 = none)',
        type: 'number',
        min: 0,
        max: 600,
        step: 5,
        default: 0,
      },
    ],
  },
  {
    type: 'obstacle',
    label: 'Obstacle',
    description: 'Solid. The player cannot move through it.',
    defaultParams: {},
    params: [],
  },
  {
    type: 'hazard',
    label: 'Hazard',
    description: 'Touching this costs a life and sends the player back to the start.',
    defaultParams: {},
    params: [],
  },
];

type HasBehaviors = Pick<SceneObject, 'behaviors'>;

export function hasBehavior(obj: HasBehaviors, type: string): boolean {
  return !!obj.behaviors?.some((b) => b.type === type);
}

export function getBehavior(obj: HasBehaviors, type: string): Behavior | undefined {
  return obj.behaviors?.find((b) => b.type === type);
}

// Set a single param on an existing behavior, returning a new behaviors array
// (never mutates input). No-op if the object doesn't have that behavior.
export function setBehaviorParam(
  current: Behavior[] | undefined,
  type: BehaviorType,
  key: string,
  value: number | boolean
): Behavior[] {
  if (!current) return [];
  return current.map((b) =>
    b.type === type ? { ...b, params: { ...b.params, [key]: value } } : b
  );
}
export function toggleBehavior(
  current: Behavior[] | undefined,
  type: BehaviorType
): Behavior[] {
  const list = current ? [...current] : [];
  const idx = list.findIndex((b) => b.type === type);
  if (idx >= 0) {
    list.splice(idx, 1);
    return list;
  }
  const meta = BEHAVIOR_CATALOG.find((m) => m.type === type);
  list.push({ type, params: { ...(meta?.defaultParams ?? {}) } });
  return list;
}
